import { useEffect, useRef, useState } from "react";
import type { GoogleConnection } from "@/hooks/use-google-connections";
import { useTranslation } from "react-i18next";

export interface GoogleCallbackSuccessResult {
  status: "connected";
  connection_id: string;
  connection?: GoogleConnection;
  return_path?: string;
}

interface UseGoogleCallbackReturn {
  state: "loading" | "success" | "error";
  result: GoogleCallbackSuccessResult | null;
  error: string | null;
}

const inflight = new Map<string, Promise<GoogleCallbackSuccessResult>>();

/** @internal Exported for callback-hook unit tests. */
export function __resetGoogleCallbackInflightForTests(): void {
  inflight.clear();
}

export function useGoogleCallback(): UseGoogleCallbackReturn {
  const { t } = useTranslation("connections");
  const [state, setState] = useState<UseGoogleCallbackReturn["state"]>("loading");
  const [result, setResult] = useState<GoogleCallbackSuccessResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const stateParam = params.get("state");
    const googleError = params.get("error");
    const googleErrorDescription = params.get("error_description");
    if (googleError) {
      setState("error");
      setError(googleErrorDescription || t("callback.googleCancelled"));
      return;
    }
    if (!code || !stateParam) {
      setState("error");
      setError(t("callback.missingCodeOrState"));
      return;
    }

    const key = `${code}:${stateParam}`;
    let promise = inflight.get(key);
    if (!promise) {
      promise = (async () => {
        const response = await fetch("/api/google/callback", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, state: stateParam }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok || body.status !== "connected") {
          throw new Error(body.error || body.detail || t("callback.failed", { status: response.status }));
        }
        return body as GoogleCallbackSuccessResult;
      })();
      inflight.set(key, promise);
    }
    promise
      .then((value) => {
        if (!mountedRef.current) return;
        setResult(value);
        setState("success");
      })
      .catch((err: any) => {
        if (!mountedRef.current) return;
        setError(err?.message ?? t("callback.network"));
        setState("error");
      });
  }, [t]);

  return { state, result, error };
}
